import React, { useState, useEffect } from 'react'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CurrencyDollarIcon,
  GiftIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { rechargeConfigApi } from '../../services/adminApi'

interface PresetAmount {
  id: number
  amount: number
  display_order: number
  is_active: number
  is_popular: number
  created_at: string
  updated_at: string
}

interface BonusRule {
  id: number
  min_amount: number
  max_amount: number | null
  bonus_type: 'percentage' | 'fixed'
  bonus_value: number
  description: string
  is_active: number
  priority: number
  created_at: string
  updated_at: string
}

interface SystemConfig {
  id: number
  min_recharge_amount: number
  allow_custom_amount: number
  created_at: string
  updated_at: string
}

export const RechargeConfigManagement: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'presets' | 'bonus' | 'system'>('presets')
  const [loading, setLoading] = useState(false)
  
  // 预设金额状态
  const [presetAmounts, setPresetAmounts] = useState<PresetAmount[]>([])
  const [showPresetModal, setShowPresetModal] = useState(false)
  const [editingPreset, setEditingPreset] = useState<PresetAmount | null>(null)
  const [presetForm, setPresetForm] = useState({
    amount: '',
    display_order: '',
    is_active: true,
    is_popular: false
  })
  
  // 赠送规则状态
  const [bonusRules, setBonusRules] = useState<BonusRule[]>([])
  const [showBonusModal, setShowBonusModal] = useState(false)
  const [editingBonus, setEditingBonus] = useState<BonusRule | null>(null)
  const [bonusForm, setBonusForm] = useState({
    min_amount: '',
    max_amount: '',
    bonus_type: 'fixed' as 'percentage' | 'fixed',
    bonus_value: '',
    description: '',
    is_active: true,
    priority: '0'
  })
  
  // 系统配置状态
  const [systemConfig, setSystemConfig] = useState<SystemConfig | null>(null)
  const [systemForm, setSystemForm] = useState({
    min_recharge_amount: '',
    allow_custom_amount: true
  })
  
  // 加载预设金额列表
  const loadPresetAmounts = async () => {
    try {
      setLoading(true)
      const response = await rechargeConfigApi.getPresetAmounts()
      if (response.success) {
        setPresetAmounts(response.data || [])
      }
    } catch (error) {
      console.error('加载预设金额失败:', error)
      toast.error('加载预设金额失败')
    } finally {
      setLoading(false)
    }
  }
  
  // 加载赠送规则列表
  const loadBonusRules = async () => {
    try {
      setLoading(true)
      const response = await rechargeConfigApi.getBonusRules()
      if (response.success) {
        setBonusRules(response.data || [])
      }
    } catch (error) {
      console.error('加载赠送规则失败:', error)
      toast.error('加载赠送规则失败')
    } finally {
      setLoading(false)
    }
  }
  
  // 加载系统配置
  const loadSystemConfig = async () => {
    try {
      setLoading(true)
      const response = await rechargeConfigApi.getSystemConfig()
      if (response.success && response.data) {
        setSystemConfig(response.data)
        setSystemForm({
          min_recharge_amount: response.data.min_recharge_amount.toString(),
          allow_custom_amount: response.data.allow_custom_amount === 1
        })
      }
    } catch (error) {
      console.error('加载系统配置失败:', error)
      toast.error('加载系统配置失败')
    } finally {
      setLoading(false)
    }
  }
  
  // 加载对应数据
  useEffect(() => {
    if (activeSection === 'presets') {
      loadPresetAmounts()
      loadBonusRules() // 同时加载赠送规则，用于在预设金额中显示
    } else if (activeSection === 'bonus') {
      loadBonusRules()
    } else if (activeSection === 'system') {
      loadSystemConfig()
    }
  }, [activeSection])
  
  // 计算预设金额对应的赠送金额
  const calculateBonus = (amount: number): { bonusAmount: number; description: string } | null => {
    if (!bonusRules || bonusRules.length === 0) return null
    
    // 找到匹配的赠送规则（按优先级和金额区间）
    const matchedRule = bonusRules.find(rule => {
      if (rule.is_active !== 1) return false
      if (amount < rule.min_amount) return false
      if (rule.max_amount !== null && amount > rule.max_amount) return false
      return true
    })
    
    if (!matchedRule) return null
    
    let bonusAmount = 0
    if (matchedRule.bonus_type === 'fixed') {
      bonusAmount = matchedRule.bonus_value
    } else if (matchedRule.bonus_type === 'percentage') {
      bonusAmount = amount * (matchedRule.bonus_value / 100)
    }
    
    return {
      bonusAmount,
      description: matchedRule.description
    }
  }
  
  // 打开新增/编辑预设金额弹窗
  const handleOpenPresetModal = (preset?: PresetAmount) => {
    if (preset) {
      setEditingPreset(preset)
      setPresetForm({
        amount: preset.amount.toString(),
        display_order: preset.display_order.toString(),
        is_active: preset.is_active === 1,
        is_popular: preset.is_popular === 1
      })
    } else {
      setEditingPreset(null)
      setPresetForm({
        amount: '',
        display_order: '0',
        is_active: true,
        is_popular: false
      })
    }
    setShowPresetModal(true)
  }
  
  // 保存预设金额
  const handleSavePreset = async () => {
    try {
      const amount = parseFloat(presetForm.amount)
      if (isNaN(amount) || amount <= 0) {
        toast.error('请输入有效的充值金额')
        return
      }
      
      const data = {
        amount,
        display_order: parseInt(presetForm.display_order) || 0,
        is_active: presetForm.is_active,
        is_popular: presetForm.is_popular
      }
      
      if (editingPreset) {
        const response = await rechargeConfigApi.updatePresetAmount(editingPreset.id, data)
        if (response.success) {
          toast.success('更新成功')
          setShowPresetModal(false)
          loadPresetAmounts()
        }
      } else {
        const response = await rechargeConfigApi.createPresetAmount(data)
        if (response.success) {
          toast.success('创建成功')
          setShowPresetModal(false)
          loadPresetAmounts()
        }
      }
    } catch (error: any) {
      console.error('保存预设金额失败:', error)
      toast.error(error.message || '保存失败')
    }
  }
  
  // 删除预设金额
  const handleDeletePreset = async (id: number) => {
    if (!confirm('确定要删除这个预设金额吗?')) return
    
    try {
      const response = await rechargeConfigApi.deletePresetAmount(id)
      if (response.success) {
        toast.success('删除成功')
        loadPresetAmounts()
      }
    } catch (error: any) {
      console.error('删除失败:', error)
      toast.error(error.message || '删除失败')
    }
  }
  
  // 打开新增/编辑赠送规则弹窗
  const handleOpenBonusModal = (bonus?: BonusRule) => {
    if (bonus) {
      setEditingBonus(bonus)
      setBonusForm({
        min_amount: bonus.min_amount.toString(),
        max_amount: bonus.max_amount?.toString() || '',
        bonus_type: bonus.bonus_type,
        bonus_value: bonus.bonus_value.toString(),
        description: bonus.description || '',
        is_active: bonus.is_active === 1,
        priority: bonus.priority.toString()
      })
    } else {
      setEditingBonus(null)
      setBonusForm({
        min_amount: '',
        max_amount: '',
        bonus_type: 'fixed',
        bonus_value: '',
        description: '',
        is_active: true,
        priority: '0'
      })
    }
    setShowBonusModal(true)
  }
  
  // 保存赠送规则
  const handleSaveBonus = async () => {
    try {
      const minAmount = parseFloat(bonusForm.min_amount)
      const maxAmount = bonusForm.max_amount ? parseFloat(bonusForm.max_amount) : null
      const bonusValue = parseFloat(bonusForm.bonus_value)
      
      if (isNaN(minAmount) || minAmount <= 0) {
        toast.error('请输入有效的最小充值金额')
        return
      }
      
      if (maxAmount && maxAmount <= minAmount) {
        toast.error('最大金额必须大于最小金额')
        return
      }
      
      if (isNaN(bonusValue) || bonusValue <= 0) {
        toast.error('请输入有效的赠送金额或比例')
        return
      }
      
      if (bonusForm.bonus_type === 'percentage' && bonusValue > 100) {
        toast.error('赠送比例不能超过100%')
        return
      }
      
      const data = {
        min_amount: minAmount,
        max_amount: maxAmount,
        bonus_type: bonusForm.bonus_type,
        bonus_value: bonusValue,
        description: bonusForm.description,
        is_active: bonusForm.is_active,
        priority: parseInt(bonusForm.priority) || 0
      }
      
      if (editingBonus) {
        const response = await rechargeConfigApi.updateBonusRule(editingBonus.id, data)
        if (response.success) {
          toast.success('更新成功')
          setShowBonusModal(false)
          loadBonusRules()
        }
      } else {
        const response = await rechargeConfigApi.createBonusRule(data)
        if (response.success) {
          toast.success('创建成功')
          setShowBonusModal(false)
          loadBonusRules()
        }
      }
    } catch (error: any) {
      console.error('保存赠送规则失败:', error)
      toast.error(error.message || '保存失败')
    }
  }
  
  // 删除赠送规则
  const handleDeleteBonus = async (id: number) => {
    if (!confirm('确定要删除这个赠送规则吗?')) return
    
    try {
      const response = await rechargeConfigApi.deleteBonusRule(id)
      if (response.success) {
        toast.success('删除成功')
        loadBonusRules()
      }
    } catch (error: any) {
      console.error('删除失败:', error)
      toast.error(error.message || '删除失败')
    }
  }
  
  // 保存系统配置
  const handleSaveSystemConfig = async () => {
    try {
      const minAmount = parseFloat(systemForm.min_recharge_amount)
      
      if (isNaN(minAmount) || minAmount <= 0) {
        toast.error('请输入有效的最低充值金额')
        return
      }
      
      const data = {
        min_recharge_amount: minAmount,
        allow_custom_amount: systemForm.allow_custom_amount
      }
      
      const response = await rechargeConfigApi.updateSystemConfig(data)
      if (response.success) {
        toast.success('保存成功')
        loadSystemConfig()
      }
    } catch (error: any) {
      console.error('保存系统配置失败:', error)
      toast.error(error.message || '保存失败')
    }
  }
  
  return (
    <div className="space-y-6">
      {/* 说明信息 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <span className="text-blue-600 mr-3">ℹ️</span>
          <div>
            <h4 className="font-medium text-blue-900">充值配置说明</h4>
            <ul className="mt-2 text-sm text-blue-700 space-y-1">
              <li>• <strong>预设金额</strong>：学生端充值时可快速选择的金额选项</li>
              <li>• <strong>赠送规则</strong>：充值满足条件时自动赠送的金额或比例</li>
              <li>• <strong>系统配置</strong>：最低充值金额限制和自定义金额开关</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* 标签切换 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveSection('presets')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeSection === 'presets'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CurrencyDollarIcon className="h-5 w-5 mr-2" />
            预设金额
          </button>
          <button
            onClick={() => setActiveSection('bonus')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeSection === 'bonus'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <GiftIcon className="h-5 w-5 mr-2" />
            赠送规则
          </button>
          <button
            onClick={() => setActiveSection('system')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeSection === 'system'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Cog6ToothIcon className="h-5 w-5 mr-2" />
            系统配置
          </button>
        </nav>
      </div>
      
      {/* 预设金额管理 */}
      {activeSection === 'presets' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">预设充值金额</h3>
            <button
              onClick={() => handleOpenPresetModal()}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              添加预设金额
            </button>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {presetAmounts.length === 0 ? (
                <div className="col-span-full text-center py-8 text-gray-500">
                  暂无预设金额
                </div>
              ) : (
                presetAmounts.map((preset) => {
                  const bonusInfo = calculateBonus(preset.amount)
                  return (
                    <div
                      key={preset.id}
                      className={`border rounded-lg p-4 ${
                        preset.is_active ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-2xl font-bold text-purple-600">
                              ¥{preset.amount}
                            </span>
                            {preset.is_popular === 1 && (
                              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                                热门
                              </span>
                            )}
                            {preset.is_active === 1 ? (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                启用
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                禁用
                              </span>
                            )}
                          </div>
                          {bonusInfo && (
                            <p className="text-sm text-red-600 font-medium mt-1">
                              赠送: ¥{bonusInfo.bonusAmount.toFixed(2)}
                            </p>
                          )}
                          <p className="text-sm text-gray-500 mt-2">
                            显示顺序: {preset.display_order}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleOpenPresetModal(preset)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePreset(preset.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          )}
        </div>
      )}
      
      {/* 赠送规则管理 */}
      {activeSection === 'bonus' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">充值赠送规则</h3>
            <button
              onClick={() => handleOpenBonusModal()}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              添加赠送规则
            </button>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <div className="space-y-3">
              {bonusRules.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  暂无赠送规则
                </div>
              ) : (
                bonusRules.map((rule) => (
                  <div
                    key={rule.id}
                    className={`border rounded-lg p-4 ${
                      rule.is_active ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-semibold text-gray-900">{rule.description}</h4>
                          {rule.is_active === 1 ? (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                              启用
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                              禁用
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">充值范围:</span>
                            <p className="font-medium">
                              ¥{rule.min_amount} {rule.max_amount ? `- ¥${rule.max_amount}` : '及以上'}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">赠送方式:</span>
                            <p className="font-medium">
                              {rule.bonus_type === 'fixed' ? '固定金额' : '百分比'}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">赠送:</span>
                            <p className="font-medium text-purple-600">
                              {rule.bonus_type === 'fixed' ? `¥${rule.bonus_value}` : `${rule.bonus_value}%`}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500">优先级:</span>
                            <p className="font-medium">{rule.priority}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleOpenBonusModal(rule)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteBonus(rule.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
      
      {/* 系统配置 */}
      {activeSection === 'system' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-6">系统配置</h3>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  最低充值金额 (元)
                </label>
                <input
                  type="number"
                  value={systemForm.min_recharge_amount}
                  onChange={(e) => setSystemForm({ ...systemForm, min_recharge_amount: e.target.value })}
                  min="0.01"
                  step="0.01"
                  className="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="0.01"
                />
                <p className="mt-1 text-sm text-gray-500">
                  学生每次充值的最低金额限制
                </p>
              </div>
              
              <div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={systemForm.allow_custom_amount}
                    onChange={(e) => setSystemForm({ ...systemForm, allow_custom_amount: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    允许学生自定义充值金额
                  </span>
                </label>
                <p className="ml-6 mt-1 text-sm text-gray-500">
                  关闭后,学生只能选择预设的充值金额
                </p>
              </div>
              
              <div className="pt-4 border-t">
                <button
                  onClick={handleSaveSystemConfig}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  保存配置
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* 预设金额编辑弹窗 */}
      {showPresetModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4 text-white rounded-t-xl">
              <h3 className="font-bold text-lg">
                {editingPreset ? '编辑预设金额' : '添加预设金额'}
              </h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  充值金额 (元) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={presetForm.amount}
                  onChange={(e) => setPresetForm({ ...presetForm, amount: e.target.value })}
                  min="0.01"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  显示顺序
                </label>
                <input
                  type="number"
                  value={presetForm.display_order}
                  onChange={(e) => setPresetForm({ ...presetForm, display_order: e.target.value })}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="0"
                />
                <p className="mt-1 text-xs text-gray-500">数字越小越靠前显示</p>
              </div>
              
              <div className="space-y-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={presetForm.is_active}
                    onChange={(e) => setPresetForm({ ...presetForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">启用此金额选项</span>
                </label>
                
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={presetForm.is_popular}
                    onChange={(e) => setPresetForm({ ...presetForm, is_popular: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">标记为热门推荐</span>
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200">
              <button
                onClick={() => setShowPresetModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSavePreset}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 赠送规则编辑弹窗 */}
      {showBonusModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4 text-white rounded-t-xl sticky top-0">
              <h3 className="font-bold text-lg">
                {editingBonus ? '编辑赠送规则' : '添加赠送规则'}
              </h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  最小充值金额 (元) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={bonusForm.min_amount}
                  onChange={(e) => setBonusForm({ ...bonusForm, min_amount: e.target.value })}
                  min="0.01"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  最大充值金额 (元)
                </label>
                <input
                  type="number"
                  value={bonusForm.max_amount}
                  onChange={(e) => setBonusForm({ ...bonusForm, max_amount: e.target.value })}
                  min="0.01"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="留空表示无上限"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  赠送方式 <span className="text-red-500">*</span>
                </label>
                <select
                  value={bonusForm.bonus_type}
                  onChange={(e) => setBonusForm({ ...bonusForm, bonus_type: e.target.value as 'percentage' | 'fixed' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="fixed">固定金额</option>
                  <option value="percentage">百分比</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {bonusForm.bonus_type === 'fixed' ? '赠送金额 (元)' : '赠送比例 (%)'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={bonusForm.bonus_value}
                  onChange={(e) => setBonusForm({ ...bonusForm, bonus_value: e.target.value })}
                  min="0.01"
                  step="0.01"
                  max={bonusForm.bonus_type === 'percentage' ? '100' : undefined}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder={bonusForm.bonus_type === 'fixed' ? '10' : '10'}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  规则描述 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={bonusForm.description}
                  onChange={(e) => setBonusForm({ ...bonusForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="充值100-199元赠送10元"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  优先级
                </label>
                <input
                  type="number"
                  value={bonusForm.priority}
                  onChange={(e) => setBonusForm({ ...bonusForm, priority: e.target.value })}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="0"
                />
                <p className="mt-1 text-xs text-gray-500">数字越大优先级越高</p>
              </div>
              
              <div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bonusForm.is_active}
                    onChange={(e) => setBonusForm({ ...bonusForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">启用此规则</span>
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200">
              <button
                onClick={() => setShowBonusModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSaveBonus}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
